import React, { useLayoutEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import styles from './ChatbotMarkdown.module.scss';

const remarkPlugins = [remarkGfm, remarkBreaks];

/** Allow GFM/code class hooks while keeping rehype-raw output sanitized. */
const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code || []),
      ['className'],
      ['class'],
    ],
    span: [...(defaultSchema.attributes?.span || []), ['className'], ['class']],
    a: [
      ...(defaultSchema.attributes?.a || []),
      ['className'],
      ['class'],
      ['target'],
      ['rel'],
    ],
  },
};

const rehypePlugins = [rehypeRaw, [rehypeSanitize, sanitizeSchema]];

function markdownChildrenToPlainText(children) {
  if (children == null || children === false) return '';
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(markdownChildrenToPlainText).join('');
  }
  if (React.isValidElement(children)) {
    return markdownChildrenToPlainText(children.props?.children);
  }
  return '';
}

/** Stacked card rows read the header from data-cell, same as the AI chat tables. */
function decorateTables(root) {
  if (!root) return;
  const tables = root.querySelectorAll('table');
  tables.forEach((table) => {
    const headers = Array.from(table.querySelectorAll('th')).map((th) =>
      (th.textContent || '').trim(),
    );
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach((row) => {
      const cells = row.querySelectorAll('td');
      if (cells.length === 0 || row.textContent.trim() === '') {
        row.remove();
        return;
      }
      cells.forEach((td, index) => {
        const header = headers[index]?.trim() || '';
        if (header) {
          td.setAttribute('data-cell', header);
        }
      });
    });
  });
}

// react-markdown 9+ renamed `uriTransformer` to `defaultUrlTransform`.
// Keep this local so CRA production builds do not depend on either export.
const SAFE_URL_PROTOCOL = /^(https?|ircs?|mailto|xmpp)$/i;

function sanitizeMarkdownUrl(value) {
  const url = value == null ? '' : String(value);
  const colon = url.indexOf(':');
  const questionMark = url.indexOf('?');
  const numberSign = url.indexOf('#');
  const slash = url.indexOf('/');

  if (
    colon === -1 ||
    (slash !== -1 && colon > slash) ||
    (questionMark !== -1 && colon > questionMark) ||
    (numberSign !== -1 && colon > numberSign) ||
    SAFE_URL_PROTOCOL.test(url.slice(0, colon))
  ) {
    return url;
  }

  return '';
}

const safeUrlTransform = (url) => {
  const transformed = sanitizeMarkdownUrl(url);
  if (!transformed) return '';

  const value = String(transformed).trim();
  if (!value) return '';
  if (value.startsWith('#') || value.startsWith('/')) return value;

  try {
    const parsed = new URL(value, 'https://example.invalid');
    if (['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)) {
      return transformed;
    }
  } catch {
    return '';
  }
  return '';
};

/**
 * Renders chatbot message Markdown (GFM + soft line breaks).
 */
const ChatbotMarkdown = ({ content, variant = 'assistant' }) => {
  const text = content == null ? '' : String(content);
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    decorateTables(rootRef.current);
  }, [text]);

  const components = useMemo(
    () => ({
      a: ({ href, children, ...props }) => {
        const external = /^https?:/i.test(String(href || ''));
        return (
          <a
            href={href}
            {...props}
            {...(external
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
          >
            {children}
          </a>
        );
      },
      code: ({ children, className, ...rest }) => {
        const match = /language-(\w+)/.exec(className || '');
        const codeText = String(children).replace(/\n$/, '');
        const isBlock = Boolean(match) || codeText.includes('\n');

        if (isBlock) {
          return (
            <pre className={styles.codeBlock}>
              <code className={className} {...rest}>
                {codeText}
              </code>
            </pre>
          );
        }

        return (
          <code className={styles.inlineCode} {...rest}>
            {children}
          </code>
        );
      },
      pre: ({ children }) => <div className={styles.preWrap}>{children}</div>,
      table: ({ children }) => (
        <div className={styles.tableScroll}>
          <table>{children}</table>
        </div>
      ),
      th: ({ children, ...props }) => (
        <th data-cell={markdownChildrenToPlainText(children)} {...props}>
          {children}
        </th>
      ),
      td: ({ children, ...props }) => (
        <td {...props}>
          <span className={styles.cellValue}>{children}</span>
        </td>
      ),
    }),
    [],
  );

  if (!text.trim()) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      className={`${styles.markdown} ${
        variant === 'user' ? styles.user : styles.assistant
      }`}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        urlTransform={safeUrlTransform}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
};

export default ChatbotMarkdown;
